'use client';

import { Copy } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '~/components/ui/dropdown-menu';
import { cn } from '~/lib/utils';
import { api } from '~/utils/api';
import { AuditEventType, AuditLogFeature } from '~/utils/auditLogTypes';
import { toast } from '~/utils/toast';

type SuggestionsProps = {
  /** Suggested texts to replace the original one */
  suggestions: string[];
  feedbackId?: string;
  isCompactView?: boolean;
};

const stripSuggestionEmoji = (text: string) =>
  text.replace(/[\u2705\u274C\u2705️]/gu, '').replace(/\s{2,}/g, ' ').trim();

const normalizeSuggestion = (text: string) => {
  const stripped = stripSuggestionEmoji(text);
  if (!stripped) return stripped;
  return stripped.charAt(0).toUpperCase() + stripped.slice(1);
};

export default function Suggestions(props: SuggestionsProps) {
  const { mutate: registerLog } = api.log.registerLog.useMutation({});

  if (!props.suggestions.length) return null;

  const count = props.suggestions.length;
  const countLabel = `${count} ${count === 1 ? 'suggestion' : 'suggestions'}`;

  return (
    <DropdownMenu
      onOpenChange={(open) => {
        if (!open) return;
        registerLog({
          description: 'View suggestions',
          eventType: AuditEventType.VIEW,
          feature: AuditLogFeature.FEEDBACK_SUGGESTION,
          recordId: props.feedbackId,
        });
      }}
    >
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-haspopup="menu"
          aria-label={`Copy ${countLabel}`}
          className={cn(
            'text-muted-foreground hover:bg-accent hover:text-foreground inline-flex h-8 items-center rounded-md px-2 text-sm font-medium',
            props.isCompactView && 'h-6 px-1.5 text-xs',
          )}
          onClick={(e) => e.stopPropagation()}
        >
          {countLabel}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        side="top"
        collisionPadding={12}
        className="w-[min(22rem,calc(100vw-2rem))] p-1"
        onClick={(e) => e.stopPropagation()}
      >
        {props.suggestions.map((suggestion, idx) => {
          const text = normalizeSuggestion(suggestion);
          return (
            <DropdownMenuItem
              key={idx}
              aria-label={`Copy suggestion: ${text}`}
              className={cn(
                'group data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground cursor-pointer items-start gap-3 whitespace-normal rounded-md px-2.5 py-2',
                idx > 0 && 'border-border/60 border-t',
                props.isCompactView ? 'text-xs' : 'text-[13px]',
              )}
              onSelect={(e) => {
                e.preventDefault();
                void navigator.clipboard.writeText(text).then(() => {
                  toast.success('Copied');
                });
              }}
            >
              <span className="text-foreground min-w-0 flex-1 font-normal leading-relaxed">
                {text}
              </span>
              <Copy
                className="text-muted-foreground mt-0.5 shrink-0 opacity-0 transition-opacity group-hover:opacity-100 group-focus:opacity-100 group-data-[highlighted]:opacity-100"
                strokeWidth={1.5}
                aria-hidden
              />
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
